// frontend/src/main.tsx
import './services/errorReporting.ts';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { VenueBrandProvider } from './contexts/VenueBrandContext.tsx';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { mixpanelInit, captureUtmParams } from './services/mixpanel.ts';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

// --- Native iOS shell setup ---
// Runs only when the app is wrapped in Capacitor (i.e. the iOS app). On the
// web build these no-op because isNativePlatform() returns false.
if (Capacitor.isNativePlatform()) {
  // Style.Dark = light (white) status-bar text/icons, which is what we want on
  // the dark navy header. Capacitor names the style by background, so
  // Style.Dark = "for dark backgrounds"; Style.Light would render dark icons.
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  // Resize the WebView with the keyboard so bottom-sheet and modal form fields
  // remain visible while editing. Matches Keyboard.resize in capacitor.config.
  Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
}

// --- Sentry Imports ---
import * as Sentry from "@sentry/react";

// --- NEW: TanStack Query Imports ---
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// --- END NEW ---

// --- Initialize Mixpanel ---
mixpanelInit();
captureUtmParams();

// --- Referral capture: ?ref= on any landing URL (share links / pasted links).
// The query-param twin of the /bar/<ref> path capture; first-touch wins. ---
import { captureReferralParam } from './utils/referralCapture.ts';
captureReferralParam();

// --- Initialize OneSignal push (no-op unless VITE_ONESIGNAL_APP_ID is set).
// Permission is requested after login, never here. ---
import { initPush } from './services/pushNotifications.ts';
initPush();

// --- Pre-rendered /blog pages: hand <head> back to the per-route <SEO> tags.
// The #prerender body block stays until the blog page has its data. ---
import { removePrerenderedHeadTags } from './utils/prerender.ts';
removePrerenderedHeadTags();

// --- NEW: Create a client for TanStack Query ---
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global options for all queries
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true, // Refetch data when the user focuses the browser window
      retry: 1, // Retry failed requests once
    },
  },
});
// --- END NEW ---

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
        fallback={<p>An error has occurred. Our team has been notified. Please try refreshing the page.</p>}
    >
      {/* --- This is the corrected part --- */}
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <AuthProvider>
            <VenueBrandProvider>
              <App />
            </VenueBrandProvider>
          </AuthProvider>
        </BrowserRouter>
        </GoogleOAuthProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
      {/* --- End of corrected part --- */}
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
